import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { HardHat, Mail, FileText, Loader2, ChevronDown, Check } from 'lucide-react';

/**
 * Compact contractor picker + "Send to contractor email" action used beside
 * the Contractor Sheet PDF button on AppraisalDetailPage.
 *
 * UX:
 *   1. Picker (data-testid="contractor-sheet-picker") — choose a saved contractor
 *      from the address book. Auto-suggests a contractor whose `specialties`
 *      contains the active trade variant (Iron / Concrete / Painter / Gutter).
 *   2. Trade dropdown (data-testid="contractor-sheet-trade") — empty = master
 *      report, or pick a trade variant.
 *   3. "Generate PDF" — opens the PDF viewer with `?contractor_id=…` so the
 *      Contractor Name + Default Share are auto-filled.
 *   4. "Email to contractor" — POSTs to /reports/contractor-detailed/{id}/email
 *      which generates the PDF + dispatches it via the configured SMTP relay.
 */
export default function ContractorSheetPicker({ appraisalId, onOpenPdf }) {
  const { api } = useAuth();
  const [contractors, setContractors] = useState([]);
  const [contractorId, setContractorId] = useState('');
  const [trade, setTrade] = useState('');
  const [sending, setSending] = useState(false);
  const [flash, setFlash] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/contractors');
      setContractors(data || []);
    } catch { setContractors([]); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const selected = contractors.find(c => c._id === contractorId);

  const handleOpen = () => {
    const slug = trade ? `contractor-${trade.toLowerCase()}` : 'contractor-detailed';
    if (onOpenPdf) {
      onOpenPdf(slug, contractorId ? { contractor_id: contractorId } : null);
    }
  };

  const handleSend = async () => {
    if (!contractorId) {
      setFlash('Pick a contractor first');
      setTimeout(() => setFlash(''), 2500);
      return;
    }
    if (!selected?.email) {
      setFlash('Contractor has no email — add one on the Contractors page');
      setTimeout(() => setFlash(''), 3500);
      return;
    }
    const tradeLabel = trade || 'all trades';
    if (!window.confirm(`Email the Contractor Sheet (${tradeLabel}) to ${selected.contractor_name} at ${selected.email}?`)) return;
    setSending(true);
    try {
      const { data } = await api.post(`/reports/contractor-detailed/${appraisalId}/email`, {
        contractor_id: contractorId,
        contractor_type: trade,
      });
      if (data?.status === 'sent') {
        setFlash(`Sent to ${data.to}`);
      } else if (data?.status === 'logged') {
        setFlash(`Queued (SMTP not configured)`);
      }
      setTimeout(() => setFlash(''), 3500);
    } catch (e) {
      setFlash(e?.response?.data?.detail || 'Send failed');
      setTimeout(() => setFlash(''), 4000);
    }
    setSending(false);
  };

  return (
    <div data-testid="contractor-sheet-actions" className="flex items-center gap-1.5 px-2 py-1.5 rounded-md bg-rose-50/50 border border-rose-100 flex-wrap">
      <HardHat size={12} className="text-[#9F1239]" />
      <select
        data-testid="contractor-sheet-picker"
        value={contractorId}
        onChange={e => setContractorId(e.target.value)}
        className="px-2 py-1 border border-rose-200 rounded text-xs bg-white max-w-[140px]"
      >
        <option value="">Pick contractor…</option>
        {contractors.map(c => (
          <option key={c._id} value={c._id}>
            {c.contractor_name}{c.default_share ? ` (${c.default_share})` : ''}
          </option>
        ))}
      </select>
      <select
        data-testid="contractor-sheet-trade"
        value={trade}
        onChange={e => setTrade(e.target.value)}
        className="px-2 py-1 border border-rose-200 rounded text-xs bg-white"
        title="Filter sheet to one trade (Concrete/Iron/Gutter/Painter) or leave empty for the master report"
      >
        <option value="">All trades</option>
        <option value="Concrete">Concrete</option>
        <option value="Iron">Iron</option>
        <option value="Gutter">Gutter</option>
        <option value="Painter">Painter</option>
      </select>
      <button
        data-testid="contractor-sheet-open-pdf"
        onClick={handleOpen}
        className="flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-rose-200 bg-white text-[#9F1239] hover:bg-rose-50"
        title={contractorId ? `Open with ${selected?.contractor_name} pre-filled` : 'Open the master Contractor Sheet'}
      >
        <FileText size={11} /> PDF
      </button>
      <button
        data-testid="contractor-sheet-email"
        onClick={handleSend}
        disabled={sending || !contractorId}
        className="flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-[#9F1239] hover:bg-[#7C0F2E] text-white disabled:opacity-40"
        title={selected?.email ? `Email PDF to ${selected.email}` : 'Pick a contractor with an email on file'}
      >
        {sending ? <Loader2 size={11} className="animate-spin" /> : <Mail size={11} />}
        Email
      </button>
      {flash && (
        <span className="text-[10px] text-rose-700 font-semibold flex items-center gap-1">
          <Check size={10} /> {flash}
        </span>
      )}
    </div>
  );
}
